import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Auction } from '../types/auction.types';
import { rankSales, resolveSale, explainResolution, SaleRole } from '../utils/currentSale';

/**
 * WHICH SALE IS THIS PAGE SHOWING — one hook, one precedence order.
 *
 * Replaces four hand-rolled copies of loadAuctions() plus a hardcoded constant.
 * Every saleless operator page calls this and gets the same three things: the
 * list for its selector, the sale to show, and enough information to TELL THE
 * OPERATOR which sale that is.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PRECEDENCE — highest wins
 *
 *   1. ?auction=<id>   an explicit link. SalePicker has always emitted these;
 *                      until now nothing read them, so clicking a specific sale
 *                      landed you silently on the default one.
 *   2. localStorage    the operator's last explicit choice on this page, in this
 *                      browser. Sticky ACROSS RELOADS but never across sales
 *                      without saying so — see the banner.
 *   3. the resolver    utils/currentSale, by role.
 *
 * A pin that no longer matches the resolver is the exact condition that let a
 * June auction id survive to August. It is never resolved silently: `isPinned`
 * and `resolvedId` are returned so the page can say so on screen, permanently,
 * with one click to move. A wrong default you can see is a five-second fix.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NOTES
 *
 * - The auctions query is deliberately UNORDERED. `orderBy('createdAt','desc')`
 *   silently omits documents missing the field, so the old code could not select
 *   a sale that lacked one. Ordering happens in memory, on a key that exists.
 * - A pinned id that no longer resolves to a real sale (deleted, wrong project)
 *   is dropped and the storage key cleared, rather than leaving the page blank.
 * - Reads the whole auctions collection, exactly as the pages it replaces did.
 *   `lots[]` is embedded in the auction doc, so the effective-end calculation
 *   costs no extra reads.
 */

const STORAGE_PREFIX = 'saleSelection:';

/** localStorage throws in private mode / when storage is disabled. Never load-bearing. */
const readPin = (key: string): string | null => {
  try { return localStorage.getItem(STORAGE_PREFIX + key); } catch { return null; }
};
const writePin = (key: string, value: string | null) => {
  try {
    if (value) localStorage.setItem(STORAGE_PREFIX + key, value);
    else localStorage.removeItem(STORAGE_PREFIX + key);
  } catch { /* ignore */ }
};

export interface SaleSelection {
  /** Every sale, newest real end first, drafts last. For the selector. */
  auctions: Auction[];
  /** The sale to show. null only while loading or if there are no sales at all. */
  auction: Auction | null;
  saleId: string;
  /** What the resolver would pick with no pin. Drives the banner. */
  resolvedId: string;
  /**
   * True when the sale on screen is NOT the one the resolver would pick — i.e.
   * a pin or a link took you somewhere else. This is the banner's trigger, so
   * it is deliberately about the DIFFERENCE, not about where the id came from:
   * pinning the sale that is already current is not a discrepancy to warn about.
   */
  isPinned: boolean;
  /** One sentence explaining the resolver's choice, for the banner tooltip. */
  reason: string;
  loading: boolean;
  error: string | null;
  /** Choose a sale. Persists as a pin. Pass null to clear and follow the resolver. */
  setSale: (id: string | null) => void;
  /**
   * Clear the pin and jump to the resolved sale. The banner’s one click.
   *
   * Deliberately NOT named `use*`. This is an event callback, not a hook, and
   * react-hooks/rules-of-hooks matches on the NAME alone — a `use`-prefixed
   * callback invoked from a click handler reads to the linter as a hook called
   * outside a component and fails the CRA build as an error, not a warning.
   */
  resetToResolved: () => void;
}

export interface UseSaleSelectionOptions {
  /**
   * 'fulfillment' for pages whose work only exists after a sale closes — the
   * Picker, Pickup Orders, the Shipping Station, the Invoice List. During a live
   * sale these must stay on the last CLOSED sale; the boxes on the bench are
   * still yesterday's.
   *
   * 'current' for pages about the sale in play.
   */
  role: SaleRole;
  /** Storage key, so the Picker and the Shipping Station can be pinned apart. */
  storageKey: string;
  /** An id from the route. Wins over everything and is never persisted. */
  routeAuctionId?: string;
}

export function useSaleSelection({
  role,
  storageKey,
  routeAuctionId,
}: UseSaleSelectionOptions): SaleSelection {
  const [searchParams, setSearchParams] = useSearchParams();
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pinned, setPinned] = useState<string | null>(() => readPin(storageKey));

  const linkedId = routeAuctionId || searchParams.get('auction') || null;

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // UNORDERED on purpose — see the note above about orderBy dropping docs.
        const snap = await getDocs(collection(db, 'auctions'));
        if (!active) return;
        setAuctions(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as Auction[]);
        setError(null);
      } catch (e: any) {
        if (!active) return;
        console.error('Failed to load auctions:', e);
        setError(e?.message || 'Could not load sales.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const byId = useMemo(() => new Map(auctions.map((a) => [a.id, a])), [auctions]);

  const resolved = useMemo(
    () => (auctions.length ? resolveSale(auctions, role) : null),
    [auctions, role],
  );
  const reason = useMemo(
    () => (auctions.length ? explainResolution(auctions, role) : ''),
    [auctions, role],
  );

  // A link beats a pin beats the resolver — but only if it names a real sale.
  const requestedId = linkedId || pinned || null;
  const requested = requestedId ? byId.get(requestedId) ?? null : null;

  // A pin pointing at a sale that no longer exists would otherwise leave the page
  // permanently blank with no way back. Drop it once the list has loaded.
  useEffect(() => {
    if (loading || !auctions.length) return;
    if (pinned && !byId.has(pinned)) {
      writePin(storageKey, null);
      setPinned(null);
    }
  }, [loading, auctions.length, pinned, byId, storageKey]);

  const auction = requested ?? resolved;
  const saleId = auction?.id ?? '';
  const resolvedId = resolved?.id ?? '';

  const setSale = useCallback((id: string | null) => {
    writePin(storageKey, id);
    setPinned(id);
    // Drop ?auction= so it can't override the choice just made.
    if (searchParams.has('auction')) {
      const next = new URLSearchParams(searchParams);
      next.delete('auction');
      setSearchParams(next, { replace: true });
    }
  }, [storageKey, searchParams, setSearchParams]);

  // Event callback, not a hook — see resetToResolved on SaleSelection for why the name matters.
  const resetToResolved = useCallback(() => setSale(null), [setSale]);

  /**
   * Selector order: the ranked sales first (newest real end), then everything the
   * ranking excludes — drafts, unstarted, no end time. Excluded from the DEFAULT,
   * never from the CHOICE.
   */
  const orderedAuctions = useMemo(() => {
    const ranked = rankSales(auctions);
    const rankedIds = new Set(ranked.map((r) => r.id));
    const rest = auctions
      .filter((a) => !rankedIds.has(a.id))
      .sort((a, b) => (a.title || a.id).localeCompare(b.title || b.id));
    return [...ranked.map((r) => r.auction), ...rest];
  }, [auctions]);

  return {
    auctions: orderedAuctions,
    auction,
    saleId,
    resolvedId,
    isPinned: !!auction && !!resolvedId && auction.id !== resolvedId,
    reason,
    loading,
    error,
    setSale,
    resetToResolved,
  };
}
